import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Pagination from '../../components/Pagination.tsx';
import { useToast } from "../../components/common/ToastContext.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { themes } from '../../theme.ts';

const WithdrawlRequest = () => {
    const { themeName } = useTheme();
    const currentTheme = themes[themeName].colors;
    const { showToast } = useToast();
    const [requests, setRequests] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
    const [notes, setNotes] = useState('');
    const [approving, setApproving] = useState(false);
    const [err, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const rawData = localStorage.getItem('user');
    const cred = rawData ? JSON.parse(rawData) : null;
    const token = cred?.access;

    // CRITICAL FIX: Ensure requests is always an array, even on first render
    const safeRequests = Array.isArray(requests) ? requests : [];
    
    // Safe pagination calculations
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedRequests = safeRequests.slice(startIndex, endIndex);
    const totalPages = Math.ceil(safeRequests.length / rowsPerPage);

    const fetchRequests = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${process.env.REACT_APP_DB_URI}/withdrawal-request/`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            
            // Log the response for debugging
            console.log('API Response:', response.data);
            
            // Handle different response structures safely
            let requestsData = [];
            
            if (response.data) {
                if (Array.isArray(response.data)) {
                    // Direct array response
                    requestsData = response.data;
                } else if (response.data.results && Array.isArray(response.data.results)) {
                    // Paginated response with results array
                    requestsData = response.data.results;
                } else if (typeof response.data === 'object' && Object.keys(response.data).length > 0) {
                    // Single object response - wrap in array
                    requestsData = [response.data];
                }
            }
            
            console.log('Processed requests data:', requestsData);
            setRequests(requestsData);
        } catch (err) {
            console.error('Error fetching requests:', err);
            setRequests([]); // Ensure requests is an array on error
            showToast("Failed to fetch withdrawal requests", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchRequests();
        } else {
            setRequests([]);
            showToast("Authentication required", "error");
        }
    }, [token]);

    const openApproveModal = (request: any) => {
        setSelectedRequest(request);
        setNotes('');
        setShowApproveModal(true);
    };

    const handleApprove = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedRequest) return;
        
        setApproving(true);
        setError(null);
        
        try {
            await axios.post(
                `${process.env.REACT_APP_DB_URI}/withdrawal-request/${selectedRequest.id}/approve/`,
                {
                    status: "APPROVED",
                    notes: notes
                },
                {
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                }
            );
            
            setShowApproveModal(false);
            setSelectedRequest(null);
            setNotes('');
            showToast("Withdrawal request approved successfully", "success");
            fetchRequests(); // Refresh the list
        } catch (err: any) {
            console.error('Error approving request:', err);
            const errorMessage = err?.response?.data?.detail || 
                               err?.response?.data?.message || 
                               err?.message || 
                               "Error approving request";
            setError(errorMessage);
            showToast(errorMessage, "error");
        } finally {
            setApproving(false);
        }
    };

    return (
        <div className="pb-4" style={{ backgroundColor: currentTheme.background }}>
            <div className="shadow-lg rounded-xl overflow-hidden" style={{ backgroundColor: currentTheme.surface }}>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm" style={{ backgroundColor: currentTheme.surface, color: currentTheme.text }}>
                        <thead className="sticky top-0 z-10" style={{ backgroundColor: currentTheme.primary }}>
                            <tr>
                                <th className="py-2 px-3 text-left" style={{ color: currentTheme.inverseText }}>ID</th>
                                <th className="py-2 px-3 text-left" style={{ color: currentTheme.inverseText }}>Amount</th>
                                <th className="py-2 px-3 text-left" style={{ color: currentTheme.inverseText }}>Status</th>
                                <th className="py-2 px-3 text-left" style={{ color: currentTheme.inverseText }}>Requested At</th>
                                <th className="py-2 px-3 text-left" style={{ color: currentTheme.inverseText }}>Notes</th>
                                <th className="py-2 px-3 text-left" style={{ color: currentTheme.inverseText }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-6" style={{ color: currentTheme.textSecondary }}>
                                        <div className="flex justify-center items-center">
                                            <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Loading...
                                        </div>
                                    </td>
                                </tr>
                            ) : paginatedRequests.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-6" style={{ color: currentTheme.textSecondary }}>
                                        No withdrawal requests found.
                                    </td>
                                </tr>
                            ) : (
                                paginatedRequests.map((req: any) => (
                                    <tr key={req?.id || Math.random()} className="hover:bg-opacity-10 hover:bg-gray-500 transition" style={{ borderBottom: `1px solid ${currentTheme.border}` }}>
                                        <td className="py-2 px-3 break-all max-w-[120px]">
                                            {req?.id ? (req.id.length > 8 ? `${req.id.slice(0, 8)}...` : req.id) : 'N/A'}
                                        </td>
                                        <td className="py-2 px-3 font-semibold" style={{ color: currentTheme.text }}>
                                            ₹{req?.amount || 0}
                                        </td>
                                        <td className="py-2 px-3">
                                            <span className="px-2 py-1 rounded text-xs font-semibold" style={{
                                                backgroundColor: req?.status === "PENDING" ? currentTheme.warning + '33' : 
                                                               req?.status === "APPROVED" ? currentTheme.success + '33' : 
                                                               currentTheme.error + '33',
                                                color: req?.status === "PENDING" ? currentTheme.warning : 
                                                       req?.status === "APPROVED" ? currentTheme.success : 
                                                       currentTheme.error
                                            }}>
                                                {req?.status || 'UNKNOWN'}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3" style={{ color: currentTheme.textSecondary }}>
                                            {req?.requested_at ? new Date(req.requested_at).toLocaleString() : 'N/A'}
                                        </td>
                                        <td className="py-2 px-3 max-w-[200px] truncate">
                                            {req?.notes || '-'}
                                        </td>
                                        <td className="py-2 px-3">
                                            {req?.status === "PENDING" && (
                                                <button
                                                    className="px-3 py-1 rounded transition hover:opacity-80"
                                                    style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
                                                    onClick={() => openApproveModal(req)}
                                                >
                                                    Approve
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls - Only show if there are items */}
                {safeRequests.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: currentTheme.border }}>
                        <div className="flex items-center gap-3">
                            <span className="text-sm" style={{ color: currentTheme.textSecondary }}>Rows per page:</span>
                            <select
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="px-3 py-1 rounded border text-sm focus:outline-none focus:ring-2"
                                style={{
                                    backgroundColor: currentTheme.surface,
                                    color: currentTheme.text,
                                    borderColor: currentTheme.border,
                                    outline: 'none'
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span className="text-sm" style={{ color: currentTheme.textMuted || currentTheme.textSecondary }}>
                                Showing {startIndex + 1}-{Math.min(endIndex, safeRequests.length)} of {safeRequests.length} requests
                            </span>
                        </div>
                        <Pagination 
                            currentPage={currentPage} 
                            totalPages={totalPages} 
                            onPageChange={setCurrentPage} 
                        />
                    </div>
                )}
            </div>

            {/* Approve Modal */}
            {showApproveModal && selectedRequest && (
                <div className="fixed inset-0 z-[991] flex items-center justify-center bg-black bg-opacity-40">
                    <div className="rounded-lg shadow-lg w-full max-w-md p-6" style={{ backgroundColor: currentTheme.surface }}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold" style={{ color: currentTheme.primary }}>
                                Approve Withdrawal Request
                            </h3>
                            <button
                                className="text-2xl font-bold hover:opacity-70"
                                style={{ color: currentTheme.textSecondary }}
                                onClick={() => {
                                    setShowApproveModal(false);
                                    setSelectedRequest(null);
                                    setNotes('');
                                    setError(null);
                                }}
                                disabled={approving}
                                type="button"
                            >
                                &times;
                            </button>
                        </div>
                        
                        <div className="mb-4 p-3 rounded" style={{ backgroundColor: currentTheme.background + '40' }}>
                            <p className="text-sm" style={{ color: currentTheme.textSecondary }}>
                                <span className="font-semibold">Amount:</span> ₹{selectedRequest?.amount || 0}
                            </p>
                            <p className="text-sm" style={{ color: currentTheme.textSecondary }}>
                                <span className="font-semibold">Request ID:</span> {selectedRequest?.id || 'N/A'}
                            </p>
                        </div>

                        <form onSubmit={handleApprove} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.text }}>
                                    Approval Notes <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    className="p-2 rounded w-full border focus:ring-2 focus:ring-opacity-50"
                                    style={{ 
                                        backgroundColor: currentTheme.background,
                                        color: currentTheme.text,
                                        borderColor: currentTheme.border,
                                        outline: 'none'
                                    }}
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    required
                                    rows={3}
                                    placeholder="Enter approval notes (required)"
                                    disabled={approving}
                                />
                            </div>

                            {err && (
                                <div className="p-3 rounded" style={{ backgroundColor: currentTheme.error + '20', color: currentTheme.error }}>
                                    <p className="text-sm font-medium">Error: {err}</p>
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowApproveModal(false);
                                        setSelectedRequest(null);
                                        setNotes('');
                                        setError(null);
                                    }}
                                    className="flex-1 py-2 rounded transition hover:opacity-80"
                                    style={{ 
                                        backgroundColor: currentTheme.border,
                                        color: currentTheme.text
                                    }}
                                    disabled={approving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2 rounded transition hover:opacity-80 disabled:opacity-50"
                                    style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
                                    disabled={approving || !notes.trim()}
                                >
                                    {approving ? (
                                        <span className="flex items-center justify-center">
                                            <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Approving...
                                        </span>
                                    ) : "Approve"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WithdrawlRequest;